import type { Configuration, IRequest } from '../..';
import HttpMethod from '../../request/HttpMethod';
import HttpStatusCode from '../../request/HttpStatusCode';
import Requester from '../../request/Requester';
import type { BatchAddress } from './Definitions/Types';
import { batchGeocodeTransformer } from './geocodeTransformer';

export interface LatLng {
  lat: number;
  lng: number;
}

// Resultado do autocomplete self-hosted (osm-enhanced /v1/geocode/autocomplete,
// proxiado pelo herbie-geocoder em /geocoder/autocomplete). OSM-39.
export interface OsmAutocompleteResult {
  display_name: string;
  main_text: string;
  secondary_text: string;
  type: string;
  lat: number;
  lon: number;
  cep?: string;
  numero?: string;
  cidade?: string;
  uf?: string;
  precision?: string;
  source?: string;
}

// Resultado do resolve-number (osm-enhanced /v1/geocode/resolve-number, gateway
// api.cobli.co direto — herbie-bouncer expõe a rota com basic:permission).
// OSM-83: refina um candidato approximate (rank>0) do autocomplete pra um ponto
// exato/interpolado dado cep+numero. `precision` ∈ exact/interpolated/approximate;
// `source` ∈ osm/mixed/qualocep.
// OSM-85: `cep` (raw, 8 dígitos) é o CEP da faixa que REALMENTE contém o
// número resolvido — pode divergir do cep do candidato original (vias com
// múltiplas faixas por prédio/trecho, ex. Av. Paulista, escolhem um cep
// arbitrário no dedup do autocomplete). `undefined` = sem faixa conhecida
// cobrindo o número; o caller mantém o cep do candidato original.
export interface ResolvedPosition {
  lat: number;
  lon: number;
  precision: string;
  source: string;
  cep?: string;
}

export class GeocodeRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getRequester = () => this.requester;

  public getAddresses = (latlng: LatLng[]): Promise<string[]> => {
    const requestData: IRequest = {
      endpoint: '/geocoder',
      method: HttpMethod.POST,
      body: JSON.stringify({ to_geocode: latlng }),
    };
    return this.requester.request(requestData).then((response) => response.data.addresses);
  };

  public geocodeFromAddress = (address: string): Promise<LatLng> => {
    const encodedAddress = encodeURIComponent(address);

    const requestData: IRequest = {
      endpoint: `/geocoder?address=${encodedAddress}`,
      method: HttpMethod.GET,
    };
    return this.requester.request(requestData).then((response) => response.data.location);
  };

  public getAddress = (latLng: LatLng, disableCache?: boolean): Promise<string> => {
    const disableCacheQueryParam = disableCache ? '&disable_cache=true' : '';
    const requestData: IRequest = {
      endpoint: `/geocoder?reverse=true&lat=${latLng.lat}&lng=${latLng.lng}${disableCacheQueryParam}&requester_name=herbie-dashboard`,
      method: HttpMethod.GET,
    };
    return this.requester
      .request(requestData, [HttpStatusCode.UNPROCESSABLE_ENTITY])
      .then((response) => response.data.address)
      .catch((error: unknown) => {
        if ((error as { response?: { status?: number } })?.response?.status === HttpStatusCode.UNPROCESSABLE_ENTITY) {
          return '';
        }
        throw error;
      });
  };

  // OSM-39: autocomplete self-hosted via proxy do herbie-geocoder. `bias` =
  // centro do mapa (proximidade). Sem match/erro, o caller cai no Google.
  // `signal` (opcional, retrocompatível): permite ao caller abortar a requisição
  // em voo quando uma query mais nova dispara (higiene de race do autocomplete).
  public autocomplete = (query: string, bias?: LatLng, signal?: AbortSignal): Promise<OsmAutocompleteResult[]> => {
    const biasParams = bias ? `&lat=${bias.lat}&lon=${bias.lng}` : '';
    const requestData: IRequest = {
      endpoint: `/geocoder/autocomplete?q=${encodeURIComponent(query)}&limit=5${biasParams}`,
      method: HttpMethod.GET,
    };
    const request = signal ? this.requester.requestWithSignal(requestData, signal) : this.requester.request(requestData);
    return request.then((response) => response.data);
  };

  // OSM-83: gateway público direto (não passa pelo herbie-geocoder proxy) —
  // `externalBaseURL` troca só o host, `request` (não `externalRequest`) mantém
  // os headers de auth Cobli (JWT), exigidos pela rota no bouncer. 204 = sem
  // via/âncora pro cep — candidato approximate original é mantido pelo caller.
  public resolveNumber = (cep: string, numero: number): Promise<ResolvedPosition | null> => {
    const requestData: IRequest = {
      endpoint: '/v1/geocode/resolve-number',
      method: HttpMethod.GET,
      params: { cep, numero },
      externalBaseURL: 'https://api.cobli.co',
    };
    return this.requester.request(requestData).then((response) => (response.status === HttpStatusCode.NO_CONTENT ? null : response.data));
  };

  public getLatLngFromAddresses = (addresses: string[]): Promise<BatchAddress[]> => {
    const body = {
      origin: 'routing',
      addresses,
    };
    const requestData: IRequest = {
      endpoint: '/geocoder/places',
      method: HttpMethod.POST,
      body: JSON.stringify(body),
    };
    return this.requester.request(requestData).then((response) => batchGeocodeTransformer(response.data, addresses));
  };
}
